import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { Bell, User, Mail, Plus, Trash2, Users, AlertCircle } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout.jsx';

// GraphQL Queries y Mutations
const GET_DEFAULT_NOTIFICATIONS = gql`
  query GetDefaultNotifications {
    users {
      id
      email
      full_name
      is_active
      role {
        id
        role_name
      }
      defaultNotifications {
        userId
      }
    }
  }
`;

const GET_ACTIVE_USERS = gql`
  query GetActiveUsers {
    users {
      id
      email
      full_name
      is_active
      role {
        id
        role_name
      }
    }
  }
`;

const ADD_DEFAULT_NOTIFICATION = gql`
  mutation AddDefaultNotification($userId: UUID!) {
    addDefaultNotification(userId: $userId) {
      userId
      user {
        id
        email
        full_name
      }
    }
  }
`;

const REMOVE_DEFAULT_NOTIFICATION = gql`
  mutation RemoveDefaultNotification($userId: UUID!) {
    removeDefaultNotification(userId: $userId)
  }
`;

const DefaultNotificationsPage = () => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Queries
  const { data: usersData, loading, error, refetch } = useQuery(GET_DEFAULT_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network'
  });

  // Mutations
  const [addDefaultNotification, { loading: addLoading }] = useMutation(ADD_DEFAULT_NOTIFICATION, {
    onCompleted: () => {
      toast.success('Usuario agregado a notificaciones por defecto');
      setIsAddModalOpen(false);
      setSelectedUsers([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const [removeDefaultNotification, { loading: removeLoading }] = useMutation(REMOVE_DEFAULT_NOTIFICATION, {
    onCompleted: () => {
      toast.success('Usuario removido de notificaciones por defecto');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  // Filtrar usuarios que ya tienen notificaciones por defecto
  const usersWithNotifications = usersData?.users?.filter(user => 
    user.defaultNotifications
  ) || [];

  // Filtrar usuarios disponibles para agregar (activos y sin notificaciones por defecto)
  const availableUsers = usersData?.users?.filter(user => 
    user.is_active && !user.defaultNotifications
  ) || [];

  // Filtrar por búsqueda
  const filteredUsers = usersWithNotifications.filter(user =>
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.role_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAvailableUsers = availableUsers.filter(user =>
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.role_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handlers
  const handleAddUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleAddNotifications = async () => {
    for (const userId of selectedUsers) {
      try {
        await addDefaultNotification({
          variables: { userId }
        });
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  };

  const handleRemoveNotification = async (userId) => {
    try {
      await removeDefaultNotification({
        variables: { userId }
      });
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedUsers([]);
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error al cargar los datos: {error.message}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <Bell className="h-6 w-6 mr-3 text-blue-600" />
            Notificaciones por Defecto
          </h1>
          <p className="text-gray-600">
            Gestiona los usuarios que reciben notificaciones automáticas del sistema
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Usuarios con Notificaciones */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-green-600" />
                  Usuarios con Notificaciones
                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    {usersWithNotifications.length}
                  </span>
                </h2>
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar usuarios..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Cargando usuarios...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay usuarios con notificaciones por defecto</p>
                  {searchTerm && <p className="text-sm mt-2">Intenta ajustar tu búsqueda</p>}
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center flex-1">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <User className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{user.full_name}</h4>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Mail className="h-3 w-3" />
                            <span>{user.email}</span>
                            <span>•</span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {user.role?.role_name}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveNotification(user.id)}
                        disabled={removeLoading}
                        className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Remover de notificaciones"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Usuarios Disponibles */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Plus className="h-5 w-5 mr-2 text-blue-600" />
                  Usuarios Disponibles
                  <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    {availableUsers.length}
                  </span>
                </h2>
                {selectedUsers.length > 0 && (
                  <button
                    onClick={handleAddNotifications}
                    disabled={addLoading}
                    className="flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar ({selectedUsers.length})
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Cargando usuarios...</p>
                </div>
              ) : filteredAvailableUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay usuarios disponibles para agregar</p>
                  {searchTerm && <p className="text-sm mt-2">Intenta ajustar tu búsqueda</p>}
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {filteredAvailableUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleAddUser(user.id)}
                        className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div className="flex items-center flex-1">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{user.full_name}</h4>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Mail className="h-3 w-3" />
                            <span>{user.email}</span>
                            <span>•</span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {user.role?.role_name}
                            </span>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 mb-1">
                Información sobre Notificaciones por Defecto
              </h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Los usuarios en esta lista recibirán notificaciones automáticas del sistema</li>
                <li>• Solo usuarios activos pueden ser agregados a notificaciones por defecto</li>
                <li>• Las notificaciones se envían cuando se aprueban eventos de envío</li>
                <li>• Los administradores siempre reciben notificaciones de nuevos usuarios registrados</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DefaultNotificationsPage;